import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Quote, Send, User } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

const StarRow = ({ rating, size = 16, interactive = false, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= (hover || rating);
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            onMouseEnter={() => interactive && setHover(n)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange && onChange(n)}
            className={interactive ? "cursor-pointer transition-transform hover:scale-125" : "cursor-default"}
            data-testid={interactive ? `star-${n}` : undefined}
            aria-label={`${n} stars`}
          >
            <Star size={size} fill={active ? "#D4AF37" : "none"} stroke={active ? "#D4AF37" : "#555"} />
          </button>
        );
      })}
    </div>
  );
};

const ReviewCard = ({ review, index, lang }) => {
  const reversed = index % 2 === 1;
  const text = lang === "el" ? review.text_el : review.text_en;

  return (
    <article
      data-testid={`review-${index}`}
      className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center py-12 md:py-16 border-b border-goldSoft ${
        reversed ? "md:[&>div:first-child]:order-2" : ""
      }`}
    >
      {/* Photo */}
      <div className="md:col-span-4">
        <div className="relative aspect-square max-w-[320px] mx-auto md:mx-0 border border-goldSoft overflow-hidden bg-surface">
          {review.photo_url ? (
            <img
              src={review.photo_url}
              alt={review.name}
              className="w-full h-full object-cover img-moody"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
            />
          ) : null}
          <div
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/30 to-black"
            style={{ display: review.photo_url ? "none" : "flex" }}
          >
            <span className="font-serif text-6xl text-gold">{initialsOf(review.name) || <User size={48} />}</span>
          </div>
          <span className="absolute bottom-3 right-3 font-cn text-gold text-2xl opacity-60">評</span>
        </div>
      </div>

      {/* Text */}
      <div className="md:col-span-8">
        <Quote size={36} className="text-primary opacity-40 mb-3" />
        <p className="text-[#E8E8E0] text-lg md:text-xl leading-relaxed italic mb-6">
          &ldquo;{text}&rdquo;
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <StarRow rating={review.rating || 5} size={18} />
          <span className="w-px h-6 bg-goldSoft" />
          <div>
            <h3 className="font-serif text-2xl text-gold">{review.name}</h3>
            {(review.title || review.class_type) && (
              <p className="text-dim text-xs uppercase tracking-widest mt-1">
                {review.title || review.class_type}
              </p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

const Reviews = () => {
  const { lang } = useLanguage();
  const t = useT(lang);
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, class_type: t.class_types[0], text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const avg =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
      : "5.0";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.text.trim().length < 10) {
      toast.error(lang === "el" ? "Το κείμενο είναι πολύ σύντομο" : "Text too short");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, { ...form, lang });
      setSubmitted(true);
      setForm({ name: "", rating: 5, class_type: t.class_types[0], text: "" });
      toast.success(
        lang === "el"
          ? "Η κριτική σας υποβλήθηκε! Θα δημοσιευθεί μετά από έλεγχο."
          : "Your review was submitted! It will be published after review."
      );
    } catch (err) {
      toast.error(lang === "el" ? "Σφάλμα υποβολής" : "Submission error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute right-[-10%] top-[-6%] cn-bg">評</div>
        <div className="container-wc relative z-10 max-w-4xl fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{lang === "el" ? "Κριτικές μαθητών" : "Student reviews"}</span>
          </div>
          <h1 className="h-display text-5xl md:text-7xl mb-6">
            {lang === "el" ? "Οι φωνές των μαθητών" : "The voices of the students"}
          </h1>
          <p className="text-dim text-lg leading-relaxed max-w-2xl mb-8">
            {lang === "el"
              ? "Τι λένε όσοι έχουν εκπαιδευτεί με τον Sifu Νίκο Αγαθόπουλο για την εμπειρία τους στα ιδιαίτερα μαθήματα Wing Chun και αυτοάμυνας."
              : "What those who have trained with Sifu Nikos Agathopoulos say about their experience in private Wing Chun and self-defense lessons."}
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <StarRow rating={5} size={22} />
            <span className="font-serif text-3xl text-gold">{avg}</span>
            <span className="text-dim text-sm">
              {lang === "el" ? `από ${reviews.length} κριτικές` : `from ${reviews.length} reviews`}
            </span>
            <button
              onClick={() => { setShowForm(true); setTimeout(() => document.getElementById("submit-review")?.scrollIntoView({ behavior: "smooth" }), 100); }}
              data-testid="scroll-to-form"
              className="btn-outline ml-auto"
            >
              {lang === "el" ? "Γράψτε κριτική" : "Write a review"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* REVIEWS LIST */}
      <section className="py-8">
        <div className="container-wc">
          {reviews.length === 0 ? (
            <p className="text-dim text-center py-20">
              {lang === "el" ? "Δεν υπάρχουν ακόμα κριτικές." : "No reviews yet."}
            </p>
          ) : (
            <div className="max-w-6xl mx-auto">
              {reviews.map((r, i) => (
                <ReviewCard key={r.id} review={r} index={i} lang={lang} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SUBMIT FORM */}
      <section id="submit-review" className="py-20 md:py-24 bg-surface border-t border-goldSoft">
        <div className="container-wc max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{lang === "el" ? "Η γνώμη σας μετράει" : "Your opinion counts"}</span>
          </div>
          <h2 className="h-display text-4xl md:text-5xl mb-4">
            {lang === "el" ? "Γράψτε τη δική σας κριτική" : "Write your own review"}
          </h2>
          <p className="text-dim mb-10 leading-relaxed">
            {lang === "el"
              ? "Είστε ή ήσασταν μαθητής/μαθήτρια; Μοιραστείτε την εμπειρία σας. Η κριτική σας θα δημοσιευθεί μετά από έλεγχο."
              : "Are you or were you a student? Share your experience. Your review will be published after moderation."}
          </p>

          {submitted ? (
            <div className="card text-center py-12">
              <span className="font-cn text-gold text-5xl block mb-4">感謝</span>
              <h3 className="font-serif text-2xl mb-3">
                {lang === "el" ? "Ευχαριστούμε!" : "Thank you!"}
              </h3>
              <p className="text-dim mb-6">
                {lang === "el"
                  ? "Η κριτική σας υποβλήθηκε επιτυχώς. Θα δημοσιευθεί μετά τον έλεγχο του Sifu."
                  : "Your review was submitted successfully. It will be published after Sifu reviews it."}
              </p>
              <button onClick={() => setSubmitted(false)} data-testid="submit-another" className="btn-outline">
                {lang === "el" ? "Νέα κριτική" : "Submit another"}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card space-y-5" data-testid="review-form">
              <div>
                <label className="eyebrow block mb-2">
                  {lang === "el" ? "Η βαθμολογία σας" : "Your rating"} *
                </label>
                <StarRow
                  rating={form.rating}
                  size={32}
                  interactive
                  onChange={(n) => setForm({ ...form, rating: n })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow block mb-2">
                    {lang === "el" ? "Ονοματεπώνυμο" : "Full name"} *
                  </label>
                  <input
                    required
                    data-testid="review-input-name"
                    className="field"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">
                    {lang === "el" ? "Τύπος μαθήματος" : "Class type"}
                  </label>
                  <select
                    data-testid="review-input-class"
                    className="field"
                    value={form.class_type}
                    onChange={(e) => setForm({ ...form, class_type: e.target.value })}
                  >
                    {t.class_types.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-2">
                  {lang === "el" ? "Η κριτική σας" : "Your review"} *
                </label>
                <textarea
                  required
                  data-testid="review-input-text"
                  className="field"
                  rows={6}
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  minLength={10}
                  maxLength={2000}
                  placeholder={lang === "el" ? "Μοιραστείτε την εμπειρία σας..." : "Share your experience..."}
                />
                <p className="text-xs text-dim mt-1">
                  {form.text.length} / 2000
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit"
                className="btn-primary disabled:opacity-50"
              >
                {submitting
                  ? (lang === "el" ? "Υποβολή..." : "Submitting...")
                  : (lang === "el" ? "Υποβολή κριτικής" : "Submit review")} <Send size={14} />
              </button>
            </form>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-goldSoft">
        <div className="container-wc text-center max-w-3xl mx-auto">
          <span className="font-cn text-gold text-5xl block mb-6">學</span>
          <h2 className="h-display text-3xl md:text-4xl mb-6">
            {lang === "el" ? "Γίνετε ο επόμενος μαθητής" : "Become the next student"}
          </h2>
          <p className="text-dim mb-8">
            {lang === "el"
              ? "Κλείστε ένα δοκιμαστικό μάθημα και ξεκινήστε το δικό σας ταξίδι στο Wing Chun."
              : "Book a trial lesson and start your own Wing Chun journey."}
          </p>
          <Link to="/contact" data-testid="reviews-cta" className="btn-primary">
            {t.hero.cta_primary} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
